import React from "react";

import { VisuallyHidden } from "../../../components/VisuallyHidden";
import {
  isAvailable,
  valuationAmountText,
  valuationMessageFor,
  valuationWarningText,
} from "../../../services/valuation";
import { DateFormat, formatCurrency, formatDate } from "../../../utils";
import { pointProvenanceOf } from "./pointProvenance";
import { HistoryDataTableProps, HistoryPointView } from "./types";

const EMPTY_CELL = "—";

// The hover title is out of reach here, so the market date it carries is read out beside the fetch time.
const sourceTextOf = (point: HistoryPointView): string => {
  const { source, priceUpdated, fxRateText } = pointProvenanceOf(point);
  const parts = [
    source,
    priceUpdated?.text,
    priceUpdated?.title,
    fxRateText,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(". ") : EMPTY_CELL;
};

const statusTextOf = (point: HistoryPointView): string => {
  const parts = point.warnings.map(valuationWarningText);
  if (!isAvailable(point.valuation)) {
    parts.push(valuationMessageFor(point.valuation));
  }
  return parts.length > 0 ? parts.join(". ") : EMPTY_CELL;
};

// No repair buttons in here: a focus stop inside a hidden table is one a sighted keyboard user can't see.
export const HistoryDataTable = <Point extends HistoryPointView>({
  caption,
  points,
  columns = [],
}: HistoryDataTableProps<Point>) => {
  const rows = points.map((point) => {
    const date = formatDate(point.date, DateFormat.DMY_SPACE);
    return {
      key: `${point.date}-${point.isAnchor}`,
      dateText: point.isAnchor ? `${date} (opening anchor)` : date,
      valueText: valuationAmountText({
        value: point.brokerValue,
        valuation: point.valuation,
      }),
      columnTexts: columns.map((column) =>
        formatCurrency(column.valueOf(point)),
      ),
      sourceText: sourceTextOf(point),
      statusText: statusTextOf(point),
    };
  });

  return (
    <VisuallyHidden>
      <table>
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Value</th>
            {columns.map((column) => (
              <th key={column.header} scope="col">
                {column.header}
              </th>
            ))}
            <th scope="col">Source</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.dateText}</th>
              <td>{row.valueText}</td>
              {row.columnTexts.map((text, index) => (
                <td key={columns[index].header}>{text}</td>
              ))}
              <td>{row.sourceText}</td>
              <td>{row.statusText}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </VisuallyHidden>
  );
};
